<dialog #dialog [id]="modalId" class="modal">
  <div class="modal-box vr-modal max-w-4xl bg-base-100">
    <header class="flex items-center justify-between mb-4">
      <h2 class="text-lg font-semibold text-primary">
        {{ definition ? 'Edit' : 'Create' }} Rule Type
        <code *ngIf="definition" class="text-sm bg-base-200 px-1.5 py-0.5 rounded ml-1">
          {{ definition.code }}
        </code>
      </h2>
      <button
        type="button"
        (click)="close()"
        class="text-base-content/70 hover:text-base-content"
        aria-label="Close"
      >
        ×
      </button>
    </header>

    <!-- Top: rule-type definition form -->
    <form *ngIf="defForm" [formGroup]="defForm" (ngSubmit)="saveDefinition()" class="space-y-3 mb-6">
      <div class="grid grid-cols-2 gap-3">
        <label class="block">
          <span class="text-sm font-medium text-primary-focus mb-1 block">
            <span class="text-error font-bold">*</span> Code
            <span class="text-base-content/60 text-xs ml-1" *ngIf="definition">(immutable)</span>
          </span>
          <input
            type="text"
            formControlName="code"
            placeholder="e.g. MY_NEW_RULE_TYPE"
            class="input input-bordered w-full h-10 min-h-10 px-3 text-sm rounded border-base-300 focus:outline-none focus:border-base-300"
          />
        </label>
        <label class="block">
          <span class="text-sm font-medium text-primary-focus mb-1 block">
            <span class="text-error font-bold">*</span> Operation
          </span>
          <ng-select
            formControlName="operation"
            [items]="operations()"
            bindLabel="code"
            bindValue="id"
            [clearable]="false"
            [searchable]="true"
          >
            <ng-template ng-option-tmp let-item="item">
              <div class="text-sm">
                <span class="font-mono">{{ item.code }}</span>
                <span class="ml-2 text-xs text-base-content/70">{{ item.kind }}</span>
              </div>
              <div class="text-xs text-base-content/70" *ngIf="item.description">
                {{ item.description }}
              </div>
            </ng-template>
          </ng-select>
        </label>
      </div>

      <label class="block">
        <span class="text-sm font-medium text-primary-focus mb-1 block">
          <span class="text-error font-bold">*</span> Label
        </span>
        <input
          type="text"
          formControlName="label"
          class="input input-bordered w-full h-10 min-h-10 px-3 text-sm rounded border-base-300 focus:outline-none focus:border-base-300"
        />
      </label>

      <label class="block">
        <span class="text-sm font-medium text-primary-focus mb-1 block">Description</span>
        <input
          type="text"
          formControlName="description"
          class="input input-bordered w-full h-10 min-h-10 px-3 text-sm rounded border-base-300 focus:outline-none focus:border-base-300"
        />
      </label>

      <div class="flex items-center gap-4">
        <label class="block flex-1">
          <span class="text-sm font-medium text-primary-focus mb-1 block">Sort order</span>
          <input
            type="number"
            formControlName="sort_order"
            class="input input-bordered w-full h-10 min-h-10 px-3 text-sm rounded border-base-300 focus:outline-none focus:border-base-300"
          />
        </label>
        <label class="flex items-center gap-2 pt-6">
          <input type="checkbox" formControlName="is_active" class="checkbox checkbox-sm" />
          <span class="text-sm font-medium text-primary-focus">Active</span>
        </label>
      </div>

      <div class="flex justify-end gap-2 pt-2">
        <button
          type="submit"
          [disabled]="defForm.invalid"
          class="h-9 px-6 border border-base-300 rounded text-sm text-dark font-medium hover:bg-info hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {{ definition ? 'Save changes' : 'Create rule type' }}
        </button>
      </div>
    </form>

    <!-- Bottom: param list + editor (only shown on edit) -->
    <ng-container *ngIf="definition">
      <!-- Operation help card: description + which param keys the
           underlying Python validator accepts. Sourced from the API's
           @register_op decorator metadata. -->
      <div
        *ngIf="selectedOperation() as op"
        class="border-t border-base-300 pt-4 mb-4"
      >
        <div class="bg-base-200 border border-base-300 rounded p-3 text-xs">
          <div class="flex items-center gap-2 mb-1">
            <span class="font-bold text-primary uppercase tracking-wide">Operation</span>
            <code class="font-mono bg-base-100 px-1.5 py-0.5 rounded border border-base-300">{{ op.code }}</code>
            <span class="text-base-content/70">·</span>
            <span class="text-base-content/70">{{ op.kind }}</span>
          </div>
          <p class="text-base-content mb-2" *ngIf="op.description">{{ op.description }}</p>

          <div class="grid grid-cols-2 gap-3">
            <div>
              <div class="text-[10px] font-bold uppercase tracking-wide text-base-content/70 mb-1">
                Required keys
                <span class="text-base-content/60 font-normal">({{ op.required_param_keys.length }})</span>
              </div>
              <div *ngIf="op.required_param_keys.length > 0; else noneRequired" class="flex flex-wrap gap-1">
                <code
                  *ngFor="let k of op.required_param_keys"
                  class="font-mono bg-base-100 px-1.5 py-0.5 rounded border border-base-300"
                  >{{ k }}</code
                >
              </div>
              <ng-template #noneRequired>
                <span class="text-base-content/60 italic">none</span>
              </ng-template>
            </div>
            <div>
              <div class="text-[10px] font-bold uppercase tracking-wide text-base-content/70 mb-1">
                Optional keys
                <span class="text-base-content/60 font-normal">({{ op.optional_param_keys.length }})</span>
              </div>
              <div *ngIf="op.optional_param_keys.length > 0; else noneOptional" class="flex flex-wrap gap-1">
                <code
                  *ngFor="let k of op.optional_param_keys"
                  class="font-mono bg-base-100 px-1.5 py-0.5 rounded border border-base-300"
                  >{{ k }}</code
                >
              </div>
              <ng-template #noneOptional>
                <span class="text-base-content/60 italic">none</span>
              </ng-template>
            </div>
          </div>

          <div *ngIf="unaddedParamKeys().length > 0" class="mt-2 pt-2 border-t border-base-300">
            <div class="text-[10px] font-bold uppercase tracking-wide text-base-content/70 mb-1">
              Not yet defined here
              <span class="text-base-content/60 font-normal">({{ unaddedParamKeys().length }})</span>
            </div>
            <div class="flex flex-wrap gap-1">
              <code
                *ngFor="let k of unaddedParamKeys()"
                class="font-mono bg-amber-50 text-amber-800 px-1.5 py-0.5 rounded border border-amber-200"
                >{{ k }}</code
              >
            </div>
          </div>
        </div>
      </div>

      <header class="flex items-center justify-between mb-2">
        <h3 class="text-sm font-bold text-primary uppercase tracking-wide">
          Params ({{ definition.param_defs.length }})
        </h3>
      </header>

      <!-- Existing params table -->
      <table class="table w-full text-sm mb-4">
        <thead>
          <tr class="h-[36px] bg-base-200">
            <th class="py-1 px-2 text-left font-bold text-primary">Key</th>
            <th class="py-1 px-2 text-left font-bold text-primary">Widget</th>
            <th class="py-1 px-2 text-left font-bold text-primary">Required</th>
            <th class="py-1 px-2 text-left font-bold text-primary">Choices</th>
            <th class="py-1 px-2 font-bold text-primary">Actions</th>
          </tr>
        </thead>
        <tbody *ngIf="definition.param_defs.length > 0; else noParams">
          <tr *ngFor="let p of definition.param_defs" class="h-[36px] even:bg-base-200">
            <td class="py-1 px-2 text-primary font-mono text-xs">{{ p.key }}</td>
            <td class="py-1 px-2 text-primary text-xs">{{ p.widget_type }}</td>
            <td class="py-1 px-2">{{ p.is_required ? 'yes' : '' }}</td>
            <td class="py-1 px-2 text-base-content/70 text-xs">
              {{ p.choices.length ? p.choices.join(', ') : '—' }}
            </td>
            <td class="py-1 px-2">
              <div class="flex items-center gap-2">
                <button
                  class="text-primary-focus hover:text-primary transition-colors"
                  (click)="beginEditParam(p)"
                  aria-label="Edit param"
                >
                  edit
                </button>
                <button
                  class="text-primary-focus hover:text-error transition-colors"
                  (click)="deleteParam(p)"
                  aria-label="Delete param"
                >
                  delete
                </button>
              </div>
            </td>
          </tr>
        </tbody>
        <ng-template #noParams>
          <tr>
            <td colspan="5" class="py-3 text-center text-xs text-base-content/70">No params defined.</td>
          </tr>
        </ng-template>
      </table>

      <!-- Param editor -->
      <form *ngIf="paramForm" [formGroup]="paramForm" (ngSubmit)="saveParam()" class="space-y-2 bg-base-200 p-3 rounded">
        <h4 class="text-xs font-bold text-primary uppercase tracking-wide">
          {{ editingParamId() !== null ? 'Edit param' : 'Add param' }}
        </h4>
        <div class="grid grid-cols-3 gap-2">
          <label class="block col-span-1">
            <span class="text-xs text-primary-focus mb-1 block">
              <span class="text-error font-bold">*</span> Key
              <span
                class="text-base-content/60 ml-1 cursor-help"
                title="Pick one of the operation's accepted keys. Type a custom value to add a key the backend doesn't yet recognize."
                >(?)</span
              >
            </span>
            <ng-select
              formControlName="key"
              [items]="paramKeyOptions()"
              [clearable]="false"
              [searchable]="true"
              [addTag]="true"
              [placeholder]="
                paramKeyOptions().length > 0
                  ? 'Pick or type a key'
                  : 'No accepted keys — type a custom key'
              "
            ></ng-select>
          </label>
          <label class="block col-span-1">
            <span class="text-xs text-primary-focus mb-1 block">Label</span>
            <input
              type="text"
              formControlName="label"
              class="input input-bordered w-full h-8 min-h-8 px-2 text-xs rounded"
            />
          </label>
          <label class="block col-span-1">
            <span class="text-xs text-primary-focus mb-1 block">
              <span class="text-error font-bold">*</span> Widget
              <span
                *ngIf="paramKeyValueSignal() && selectedOperation()?.param_spec?.[paramKeyValueSignal()]?.widgets?.length"
                class="text-base-content/60 ml-1 cursor-help"
                title="Restricted to widgets the operation accepts for this key. Pick the key first to see the full list."
                >(restricted)</span
              >
            </span>
            <ng-select
              formControlName="widget_type"
              [items]="availableWidgetTypes()"
              [clearable]="false"
            ></ng-select>
          </label>
        </div>

        <label class="block">
          <span class="text-xs text-primary-focus mb-1 block">Help text</span>
          <input
            type="text"
            formControlName="help_text"
            class="input input-bordered w-full h-8 min-h-8 px-2 text-xs rounded"
          />
        </label>

        <div class="grid grid-cols-3 gap-2 items-end">
          <label class="block col-span-1">
            <span class="text-xs text-primary-focus mb-1 block">Default value (JSON)</span>
            <input
              type="text"
              formControlName="default_value"
              placeholder='e.g. true, "foo", 5'
              class="input input-bordered w-full h-8 min-h-8 px-2 text-xs rounded"
            />
          </label>
          <label class="block col-span-1">
            <span class="text-xs text-primary-focus mb-1 block">Sort order</span>
            <input
              type="number"
              formControlName="sort_order"
              class="input input-bordered w-full h-8 min-h-8 px-2 text-xs rounded"
            />
          </label>
          <label class="flex items-center gap-2 col-span-1">
            <input type="checkbox" formControlName="is_required" class="checkbox checkbox-sm" />
            <span class="text-xs text-primary-focus">Required</span>
          </label>
        </div>

        <label class="block" *ngIf="needsChoices()">
          <span class="text-xs text-primary-focus mb-1 block">
            <span class="text-error font-bold">*</span> Choices (SELECT / MULTI_SELECT only)
          </span>
          <ng-select
            formControlName="choices"
            [items]="[]"
            [multiple]="true"
            [addTag]="true"
            [hideSelected]="true"
            placeholder="Type a choice and press Enter"
          ></ng-select>
        </label>

        <div class="flex justify-end gap-2 pt-2">
          <button
            *ngIf="editingParamId() !== null"
            type="button"
            (click)="cancelEditParam()"
            class="h-8 px-3 border border-base-300 rounded text-xs text-dark hover:bg-base-200 transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            [disabled]="paramForm.invalid"
            class="h-8 px-4 border border-base-300 rounded text-xs text-dark font-medium hover:bg-info hover:text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {{ editingParamId() !== null ? 'Save param' : 'Add param' }}
          </button>
        </div>
      </form>
    </ng-container>
  </div>
  <form method="dialog" class="modal-backdrop">
    <button (click)="close()">close</button>
  </form>
</dialog>
